import React, { useState, useMemo, useRef } from 'react';
import { X, ScanLine, CheckCircle2, StopCircle, RefreshCw, Search, Square, CheckSquare } from 'lucide-react';
import { MindMapNode, Notebook, Language } from '../types';
import { generateReviewQuestions } from '../services/geminiService';

type Scope = 'recent' | 'manual' | 'lowest_score';
type Phase = 'config' | 'running' | 'done';

interface BackfillQuestionsModalProps {
    notebook: Notebook;
    language: Language;
    onClose: () => void;
    onNodeUpdated: (nodeId: string, count: number) => void;
}

const MIN_CONTENT_LENGTH = 150;
const MIN_SCORE = 60;

const BATCH_OPTIONS = [5, 10, 20] as const;
const QPN_OPTIONS = [
    { value: 2, label: ['Nhanh', 'Quick'] },
    { value: 4, label: ['Chuẩn', 'Standard'] },
    { value: 6, label: ['Sâu',   'Deep'] },
] as const;

export const BackfillQuestionsModal: React.FC<BackfillQuestionsModalProps> = ({
    notebook, language, onClose, onNodeUpdated,
}) => {
    const vi = language === 'vietnamese';

    // All checked notes without questions
    const allChecked = useMemo(() =>
        notebook.nodes.filter(n => n.analysis !== null && !n.reviewQuestionCount),
    [notebook.nodes]);

    // Apply eligibility heuristic: content long enough + score good enough
    const allEligible = useMemo(() =>
        allChecked.filter(n =>
            (n.content?.length ?? 0) >= MIN_CONTENT_LENGTH &&
            (n.analysis?.score ?? 0) >= MIN_SCORE
        ),
    [allChecked]);

    const skippedCount = allChecked.length - allEligible.length;

    const [scope,        setScope]        = useState<Scope>('recent');
    const [batchSize,    setBatchSize]    = useState<5 | 10 | 20>(10);
    const [qPerNote,     setQPerNote]     = useState<2 | 4 | 6>(4);
    const [manualSearch, setManualSearch] = useState('');
    const [selected,     setSelected]     = useState<Set<string>>(new Set());

    const [phase,     setPhase]     = useState<Phase>('config');
    const [done,      setDone]      = useState(0);
    const [total,     setTotal]     = useState(0);
    const [success,   setSuccess]   = useState(0);
    const [totalQ,    setTotalQ]    = useState(0);
    const [current,   setCurrent]   = useState('');
    const [remaining, setRemaining] = useState(0);
    const cancelRef = useRef(false);

    // Filtered list for manual picker
    const filteredEligible = useMemo(() => {
        const q = manualSearch.toLowerCase().trim();
        if (!q) return allEligible;
        return allEligible.filter(n => n.title.toLowerCase().includes(q));
    }, [allEligible, manualSearch]);

    const toggleNode = (id: string) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === filteredEligible.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filteredEligible.map(n => n.id)));
        }
    };

    // Compute batch that will be processed
    const batch = useMemo<MindMapNode[]>(() => {
        if (scope === 'manual') {
            return allEligible.filter(n => selected.has(n.id));
        }
        let pool = [...allEligible];
        if (scope === 'lowest_score') {
            pool.sort((a, b) => (a.analysis?.score ?? 100) - (b.analysis?.score ?? 100));
        } else {
            pool.sort((a, b) => (b.lastAICheckAt ?? 0) - (a.lastAICheckAt ?? 0));
        }
        return pool.slice(0, batchSize);
    }, [allEligible, scope, batchSize, selected]);

    const start = async () => {
        if (batch.length === 0) return;
        cancelRef.current = false;
        setPhase('running');
        setDone(0);
        setTotal(batch.length);
        setSuccess(0);
        setTotalQ(0);

        let successCount = 0;
        let totalQuestions = 0;
        const processedIds = new Set<string>();

        for (let i = 0; i < batch.length; i++) {
            if (cancelRef.current) break;
            const node = batch[i];
            setCurrent(node.title);
            try {
                const result = await generateReviewQuestions(
                    node.id, notebook.id, node.title, notebook.title,
                    node.content, node.imageBase64 ?? null, node.imageMimeType ?? null,
                    language, qPerNote, 'initial_ai_check',
                    undefined, undefined, undefined, false
                );
                onNodeUpdated(node.id, result.storedCount);
                processedIds.add(node.id);
                successCount++;
                totalQuestions += result.storedCount;
            } catch {
                // skip and continue
            }
            setDone(i + 1);
            setSuccess(successCount);
            setTotalQ(totalQuestions);
        }

        const stillMissing = allEligible.filter(n => !processedIds.has(n.id)).length;
        setRemaining(stillMissing);
        // Clear manual selection after run
        if (scope === 'manual') setSelected(new Set());
        setPhase('done');
    };

    const continueNext = () => setPhase('config');

    const scopeOptions: { val: Scope; label: string; hint: string }[] = [
        {
            val: 'recent',
            label: vi ? 'Mới AI Check gần nhất' : 'Most recently checked',
            hint: `${Math.min(batchSize, allEligible.length)} ${vi ? 'ghi chú' : 'notes'}`,
        },
        {
            val: 'manual',
            label: vi ? 'Chọn ghi chú cụ thể' : 'Pick specific notes',
            hint: selected.size > 0 ? `${selected.size} ${vi ? 'đã chọn' : 'selected'}` : vi ? 'Chưa chọn' : 'None selected',
        },
        {
            val: 'lowest_score',
            label: vi ? 'Điểm AI Check thấp nhất' : 'Lowest AI Check score',
            hint: `${Math.min(batchSize, allEligible.length)} ${vi ? 'ghi chú' : 'notes'}`,
        },
    ];

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className="bg-white dark:bg-slate-900 w-full max-w-sm rounded-2xl shadow-2xl flex flex-col overflow-hidden"
                style={{ maxHeight: '90vh' }}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 pt-5 pb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <ScanLine size={16} className="text-teal-500" />
                        <span className="text-sm font-bold text-gray-800 dark:text-white">
                            {vi ? 'Tạo câu hỏi còn thiếu' : 'Generate Missing Questions'}
                        </span>
                    </div>
                    <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full">
                        <X size={16} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 pb-5 flex flex-col gap-5">

                    {/* ── CONFIG phase ── */}
                    {phase === 'config' && (
                        <>
                            <div className="flex flex-col gap-1 -mt-2">
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {vi
                                        ? <><span className="font-bold text-gray-700 dark:text-gray-200">{allEligible.length}</span> ghi chú đủ điều kiện tạo câu hỏi.</>
                                        : <><span className="font-bold text-gray-700 dark:text-gray-200">{allEligible.length}</span> notes eligible for question generation.</>
                                    }
                                </p>
                                {skippedCount > 0 && (
                                    <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                        {vi
                                            ? `${skippedCount} ghi chú bị bỏ qua (nội dung quá ngắn hoặc điểm < ${MIN_SCORE}%).`
                                            : `${skippedCount} notes skipped (too short or score < ${MIN_SCORE}%).`
                                        }
                                    </p>
                                )}
                            </div>

                            {allEligible.length === 0 ? (
                                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium text-center py-2">
                                    {vi ? '🎉 Tất cả ghi chú đã có câu hỏi rồi!' : '🎉 All checked notes already have questions!'}
                                </p>
                            ) : (
                                <>
                                    {/* Scope selector */}
                                    <div>
                                        <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                            {vi ? 'Ưu tiên ghi chú nào' : 'Prioritize which notes'}
                                        </p>
                                        <div className="flex flex-col gap-1.5">
                                            {scopeOptions.map(({ val, label, hint }) => (
                                                <button
                                                    key={val}
                                                    onClick={() => setScope(val)}
                                                    className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs transition-colors ${
                                                        scope === val
                                                            ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 font-semibold'
                                                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                                                    }`}
                                                >
                                                    <span>{label}</span>
                                                    <span className="text-[10px] opacity-60">{hint}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Manual picker */}
                                    {scope === 'manual' && (
                                        <div className="flex flex-col gap-2">
                                            {/* Search + toggle all */}
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1 relative">
                                                    <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder={vi ? 'Tìm ghi chú...' : 'Search notes...'}
                                                        value={manualSearch}
                                                        onChange={e => setManualSearch(e.target.value)}
                                                        className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 focus:outline-none focus:border-teal-400"
                                                    />
                                                </div>
                                                <button
                                                    onClick={toggleAll}
                                                    className="flex items-center gap-1 px-2 py-1.5 text-[10px] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors flex-shrink-0"
                                                >
                                                    {selected.size === filteredEligible.length && filteredEligible.length > 0
                                                        ? <><CheckSquare size={11} /> {vi ? 'Bỏ chọn' : 'Deselect'}</>
                                                        : <><Square size={11} /> {vi ? 'Chọn tất' : 'Select all'}</>
                                                    }
                                                </button>
                                            </div>

                                            {/* Node list */}
                                            <div className="max-h-44 overflow-y-auto flex flex-col gap-0.5 border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden">
                                                {filteredEligible.length === 0 && (
                                                    <p className="text-xs text-gray-400 text-center py-4">
                                                        {vi ? 'Không tìm thấy ghi chú nào.' : 'No notes found.'}
                                                    </p>
                                                )}
                                                {filteredEligible.map(node => {
                                                    const isSelected = selected.has(node.id);
                                                    const score = node.analysis?.score;
                                                    return (
                                                        <button
                                                            key={node.id}
                                                            onClick={() => toggleNode(node.id)}
                                                            className={`flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                                                                isSelected
                                                                    ? 'bg-teal-50 dark:bg-teal-900/20'
                                                                    : 'hover:bg-gray-50 dark:hover:bg-slate-800/50'
                                                            }`}
                                                        >
                                                            <div className={`w-3.5 h-3.5 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                                                                isSelected
                                                                    ? 'border-teal-500 bg-teal-500'
                                                                    : 'border-gray-300 dark:border-slate-600'
                                                            }`}>
                                                                {isSelected && <div className="w-1.5 h-1.5 rounded-sm bg-white" />}
                                                            </div>
                                                            <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 line-clamp-1">{node.title}</span>
                                                            {score !== undefined && (
                                                                <span className={`text-[10px] font-bold flex-shrink-0 ${
                                                                    score >= 80 ? 'text-emerald-500' :
                                                                    score >= 50 ? 'text-amber-500' : 'text-red-400'
                                                                }`}>{score}</span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Batch size — hidden when manual */}
                                    {scope !== 'manual' && (
                                        <div>
                                            <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                                {vi ? 'Số ghi chú mỗi lần' : 'Notes per batch'}
                                            </p>
                                            <div className="flex gap-2">
                                                {BATCH_OPTIONS.map(n => (
                                                    <button
                                                        key={n}
                                                        onClick={() => setBatchSize(n)}
                                                        className={`flex-1 py-1.5 rounded-lg border text-xs font-bold transition-colors ${
                                                            batchSize === n
                                                                ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                                                                : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                                                        }`}
                                                    >
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Questions per note */}
                                    <div>
                                        <p className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                                            {vi ? 'Số câu mỗi ghi chú' : 'Questions per note'}
                                        </p>
                                        <div className="flex gap-2">
                                            {QPN_OPTIONS.map(({ value, label }) => (
                                                <button
                                                    key={value}
                                                    onClick={() => setQPerNote(value)}
                                                    className={`flex-1 py-1.5 rounded-lg border text-xs font-bold transition-colors ${
                                                        qPerNote === value
                                                            ? 'border-teal-400 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300'
                                                            : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'
                                                    }`}
                                                >
                                                    {vi ? label[0] : label[1]}
                                                    <span className="block text-[9px] font-normal opacity-60">{value} {vi ? 'câu' : 'q'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Estimate */}
                                    <p className="text-[11px] text-gray-400 text-center -mb-1">
                                        {vi
                                            ? <>{vi ? 'Ước tính: ' : 'Estimate: '}<span className="font-bold text-gray-600 dark:text-gray-300">{batch.length} ghi chú</span> × {qPerNote} = <span className="font-bold text-teal-600">{batch.length * qPerNote} câu hỏi</span></>
                                            : <>Estimate: <span className="font-bold text-gray-600 dark:text-gray-300">{batch.length} notes</span> × {qPerNote} = <span className="font-bold text-teal-600">{batch.length * qPerNote} questions</span></>
                                        }
                                    </p>

                                    {/* Start */}
                                    <button
                                        onClick={start}
                                        disabled={batch.length === 0}
                                        className="w-full py-2.5 bg-teal-500 hover:bg-teal-400 disabled:opacity-40 text-white text-sm font-bold rounded-xl transition-colors"
                                    >
                                        {vi ? 'Bắt đầu tạo' : 'Start generating'}
                                    </button>
                                </>
                            )}
                        </>
                    )}

                    {/* ── RUNNING phase ── */}
                    {phase === 'running' && (
                        <div className="flex flex-col gap-4 py-2">
                            <div>
                                <div className="flex justify-between text-xs mb-1.5">
                                    <span className="text-gray-500 dark:text-gray-400">
                                        {vi ? `Đang tạo ${done}/${total} ghi chú...` : `Generating ${done}/${total} notes...`}
                                    </span>
                                    <span className="text-teal-600 dark:text-teal-400 font-bold">{Math.round(done / total * 100)}%</span>
                                </div>
                                <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-teal-500 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.round(done / total * 100)}%` }}
                                    />
                                </div>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                                {vi ? 'Đang xử lý: ' : 'Processing: '}
                                <span className="text-gray-700 dark:text-gray-300 font-medium">{current}</span>
                            </p>
                            <button
                                onClick={() => { cancelRef.current = true; }}
                                className="mx-auto flex items-center gap-1.5 px-4 py-1.5 text-xs text-red-500 hover:text-red-700 dark:hover:text-red-400 border border-red-200 dark:border-red-800/40 rounded-lg transition-colors"
                            >
                                <StopCircle size={12} /> {vi ? 'Dừng lại' : 'Cancel'}
                            </button>
                        </div>
                    )}

                    {/* ── DONE phase ── */}
                    {phase === 'done' && (
                        <div className="flex flex-col gap-4 py-1">
                            <div className="flex items-center gap-3">
                                <CheckCircle2 size={28} className="text-teal-500 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-bold text-gray-800 dark:text-white">
                                        {vi ? 'Hoàn thành!' : 'Done!'}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {vi
                                            ? `Đã tạo ${totalQ} câu hỏi cho ${success}/${total} ghi chú.`
                                            : `Generated ${totalQ} questions for ${success}/${total} notes.`
                                        }
                                    </p>
                                </div>
                            </div>

                            {remaining > 0 && (
                                <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2">
                                    {vi
                                        ? `Còn ${remaining} ghi chú chưa có câu hỏi.`
                                        : `${remaining} notes still missing questions.`
                                    }
                                </p>
                            )}

                            <div className="flex gap-2">
                                {remaining > 0 && (
                                    <button
                                        onClick={continueNext}
                                        className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-teal-500 hover:bg-teal-400 text-white text-xs font-bold rounded-xl transition-colors"
                                    >
                                        <RefreshCw size={12} />
                                        {vi ? `Tiếp tục` : `Continue`}
                                    </button>
                                )}
                                <button
                                    onClick={onClose}
                                    className={`${remaining > 0 ? '' : 'flex-1'} px-4 py-2 border border-gray-200 dark:border-slate-700 text-xs font-bold text-gray-600 dark:text-gray-400 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors`}
                                >
                                    {vi ? 'Xong' : 'Done'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
